import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Dialog from "../../base/dialog";
import {Box, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import Button from "../../base/button/button";
import Form, {MuiRFF, Yup} from '../../form';
import Grid2 from "@mui/material/Unstable_Grid2";
import DateUtils from "../../../../core/services/date-utils";
import {routeFunctions} from "../../../../core/models/static/routes/app-routes";
import ToggleButtons from "../../base/button/toggle_buttons";
import {openLinkInNewTab} from "../../../../core/services/utils/utils";

const schema = Yup.object().shape({
    startDate: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .nullable(),
    endDate: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const dateOptionsValues = {
    last2Weeks: 'last2Weeks',
    last30Days: 'last30Days',
    last60Days: 'last60Days',
    last90Days: 'last90Days',
}

const PrintCustomerOutstandingStatementDialog = ({open, close, customerId, customerName}) => {
    const [dateOption, setDateOption] = useState();

    /**@type {React.MutableRefObject<formApi>}*/
    const formApiRef = useRef();

    const initialValues = useMemo(() => ({
        'startDate': null,
        'endDate': null,
    }), [])

    /**
     * Resets the date option when the dialog is opened.
     */
    useEffect(() => {
        if (open) {
            setDateOption(undefined);
        }
    }, [open]);

    /**
     * Changes the date input values based on the selected toggle button option
     */
    const changeDateByToggleButtons = useCallback((value) => {
        setDateOption(value);
        switch (value) {
            case dateOptionsValues.last2Weeks:
                formApiRef.current?.change('startDate', DateUtils.serverNow().subtract(2, 'weeks').startOf('day'));
                formApiRef.current?.change('endDate', DateUtils.serverNow());
                break;
            case dateOptionsValues.last30Days:
                formApiRef.current?.change('startDate', DateUtils.serverNow().subtract(30, 'days').startOf('day'));
                formApiRef.current?.change('endDate', DateUtils.serverNow());
                break;
            case dateOptionsValues.last60Days:
                formApiRef.current?.change('startDate', DateUtils.serverNow().subtract(60, 'days').startOf('day'));
                formApiRef.current?.change('endDate', DateUtils.serverNow());
                break;
            case dateOptionsValues.last90Days:
                formApiRef.current?.change('startDate', DateUtils.serverNow().subtract(90, 'days').startOf('day'));
                formApiRef.current?.change('endDate', DateUtils.serverNow());
                break;
            default:
                break;
        }
    }, [])

    const predefinedDateOptions = useMemo(() => [
        {
            value: dateOptionsValues.last2Weeks,
            name: "Last 2 weeks",
            disabled: false,
        },
        {
            value: dateOptionsValues.last30Days,
            name: "Last 30 days",
            disabled: false,
        },
        {
            value: dateOptionsValues.last60Days,
            name: "Last 60 days",
            disabled: false,
        },
        {
            value: dateOptionsValues.last90Days,
            name: "Last 90 days",
            disabled: false,
        },
    ], [])

    /**
     * Navigates to the print page with the provided dates or without them.
     * @type {function(*=): void}
     */
    const navigateToPrint = useCallback((dates = undefined) => {
        let url = routeFunctions.print.customerOutstandingStatement(customerId);
        if (!!dates) {
            const queryParams = new URLSearchParams();
            if (dates.startDate) {
                queryParams.set('startDate', dates.startDate);
            }
            if (dates.endDate) {
                queryParams.set('endDate', dates.endDate);
            }
            url += `?${queryParams.toString()}`;
        }
        openLinkInNewTab(window.location.origin + url);
    }, [customerId])

    /**
     * gets the form data necessary for registration of a new customer.
     * The form info includes, countries, provinces, price levels, payment terms,
     * @type {function(): Promise<void>}
     */
    const submitForm = useCallback(async (values) => {
        navigateToPrint({
            startDate: DateUtils.UTCToServer(values.startDate).format(DateUtils.withoutOffsetFormat),
            endDate: DateUtils.UTCToServer(values.endDate).format(DateUtils.withoutOffsetFormat),
        })
    }, [navigateToPrint])


    return (
        <Dialog open={open} setOpen={close} maxWidth={'sm'}>
            <DialogTitle>
                Print {customerName}'s Outstanding Statement
            </DialogTitle>
            <Form
                initialValues={initialValues}
                onSubmit={submitForm}
                validate={validate}
                formProps={{id: 'print-customer-outstanding-statement-form'}}
            >
                {({form, values}) => {
                    formApiRef.current = form;
                    return (
                        <>
                            <DialogContent dividers sx={{paddingBlock: 4}}>
                                <DialogContentText className={'mb-4'} variant={'body2'}>
                                    You can print the outstanding statement for {customerName} by selecting a date range or print all outstanding statements.
                                    Note that you can only view the "Print with dates" option if you have selected a valid date range.
                                </DialogContentText>
                                <Grid2 spacing={2} container>
                                    <Grid2 xs={12} className={'toggle-buttons'}>
                                        <ToggleButtons
                                            value={dateOption}
                                            onChange={(_, v) => changeDateByToggleButtons(v)}
                                            options={predefinedDateOptions}
                                        />
                                    </Grid2>
                                    <Grid2 xs={12} md={6}>
                                        <MuiRFF.DatePicker
                                            name={'startDate'}
                                            label={"Start Date"}
                                            required={required.startDate}
                                            maxDate={values.endDate}
                                            disableFuture
                                            onChange={(e) => setDateOption(null) || form.change('startDate', e)}
                                        />
                                    </Grid2>
                                    <Grid2 xs={12} md={6}>
                                        <MuiRFF.DatePicker
                                            name={'endDate'}
                                            label={"End Date"}
                                            required
                                            minDate={values.startDate}
                                            disableFuture
                                            onChange={(e) => setDateOption(null) || form.change('endDate', e)}
                                        />
                                    </Grid2>
                                </Grid2>
                            </DialogContent>
                            <DialogActions className={'p-0'}>
                                <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                                    <Button
                                        variant={'dialog'}
                                        color={'primary'}
                                        onClick={() => close()}
                                        borderRadius={'0 8px 0 8px'}
                                    >
                                        Close
                                    </Button>
                                    <Box>
                                        <Button
                                            disabled={!open}
                                            variant={'dialog'}
                                            color={'primary'}
                                            onClick={() => navigateToPrint()}
                                            borderRadius={'8px 0 0 0'}
                                        >
                                            Print all
                                        </Button>
                                        {
                                            ((!!values.startDate && values.startDate.isValid()) || (!!values.endDate && values.endDate.isValid())) &&
                                            <Button
                                                disabled={!open}
                                                variant={'dialog'}
                                                color={'primary'}
                                                type={'submit'}
                                                form={'print-customer-outstanding-statement-form'}
                                                borderRadius={'0 0 8px 0'}
                                            >
                                                Print with dates
                                            </Button>
                                        }
                                    </Box>
                                </Box>
                            </DialogActions>
                        </>
                    )
                }}
            </Form>

        </Dialog>
    );
}
export default PrintCustomerOutstandingStatementDialog;
